import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useContactModal } from "@/components/ContactModalProvider";
import RichText from "@/components/RichText";
import { CONTAINER, lines } from "../tokens";

// Tous les textes visibles du formulaire (libellés, exemples de saisie,
// messages du bouton) sont des CHAMPS DU THÈME, avec le texte d'origine en
// repli. Écrits en dur, ils échappaient au module multilingue : le formulaire
// restait en français sur une page passée en arabe, alors que le bouton
// d'envoi — lui déjà éditable — était bien traduit.
//
// Formulaire de devis du handoff : Nom, Email, « Plan envisagé » en
// pastilles à choix unique, puis le projet. Le plan choisi est replié dans
// le message envoyé — même endpoint /contact-request que partout ailleurs
// sur le site, il n'existe pas de champ dédié côté serveur et en inventer
// un rendrait ces demandes invisibles dans l'admin.
//
// Le handoff ne fait que basculer un état local à l'envoi ; ici la demande
// part réellement, avec l'état « envoi en cours » et le libellé de
// confirmation que son README demande d'implémenter.
const FIELD =
  "w-full bg-ink/[0.04] border-control border-ink/[0.16] rounded-btn py-3 px-[13px] text-[15px] text-ink outline-none focus:border-gold transition-colors";
const LABEL = "text-[13px] text-ink/[0.55] mb-[7px]";

// Chaque champ d'identité est "required" / "optional" / "hidden" (voir
// DEFAULT_PROPS.contact côté serveur). Repli "required" pour le nom et
// l'email — le comportement du handoff — et "optional" pour le téléphone,
// qu'il n'avait pas.
// Un vrai <label> qui ENVELOPPE son champ, et non un <div> décoratif :
// l'association est alors implicite, sans identifiant à gérer. Un lecteur
// d'écran annonce le libellé en arrivant sur le champ, et cliquer le texte y
// place le curseur — deux comportements que la version en <div> ne donnait
// pas, alors que c'est le formulaire de conversion principal du site.
const Field = ({ mode, label, children }) => {
  if (mode === "hidden") return null;
  return (
    <label className="block">
      <span className={`block ${LABEL}`}>
        {label}
        {mode === "optional" && <span className="text-ink/40"> (facultatif)</span>}
      </span>
      {children}
    </label>
  );
};

export default function ContactSection({ props = {} }) {
  const planOptions = lines(props.plan_options);
  const nameMode = props.field_name || "required";
  const emailMode = props.field_email || "required";
  const phoneMode = props.field_phone || "optional";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [plan, setPlan] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | sending | sent
  const { notifySent } = useContactModal();
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/contact-request", {
        // Un champ masqué n'envoie rien plutôt qu'une valeur restée en
        // mémoire si l'admin le masque après une saisie.
        name: nameMode === "hidden" ? "" : form.name,
        email: emailMode === "hidden" ? "" : form.email,
        phone: phoneMode === "hidden" ? "" : form.phone,
        message: plan ? `Plan envisagé : ${plan}\n\n${form.message}` : form.message,
        source: "home",
      });
      setStatus("sent");
      notifySent("", { name: nameMode === "hidden" ? "" : form.name });
      setForm({ name: "", email: "", phone: "", message: "" });
      setPlan(null);
    } catch (err) {
      setStatus("idle");
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'envoi, réessayez.");
    }
  };

  return (
    <section id="contact" className="border-t border-ink/[0.09] scroll-mt-24">
      <div className={`${CONTAINER} pt-[88px] pb-[92px] grid md:grid-cols-2 gap-14 items-start`}>
        <div>
          <RichText
            as="h2"
            className="block font-display font-semibold text-[clamp(30px,3.7vw,46px)] leading-[1.04] tracking-[-0.03em] text-ink [text-wrap:pretty]"
            html={props.heading}
          />
          <RichText
            as="p"
            preserveParagraphs
            className="mt-5 text-[16.5px] leading-[1.6] text-ink/[0.62] max-w-[420px]"
            html={props.body}
          />
          <div className="flex flex-col gap-2.5 mt-7 text-[15px] text-ink/70">
            {props.email && (
              <a href={`mailto:${props.email}`} className="hover:text-ink transition-colors w-fit">
                {props.email}
              </a>
            )}
            {props.phone && (
              <div>
                <a
                  href={`tel:${String(props.phone).replace(/[^\d+]/g, "")}`}
                  className="hover:text-ink transition-colors"
                >
                  {props.phone}
                </a>
                {props.phone_note ? ` — ${props.phone_note}` : ""}
              </div>
            )}
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="border-control border-ink/[0.12] rounded-card p-[26px] bg-ink/[0.03] flex flex-col gap-3.5"
        >
          <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]">
            <Field mode={nameMode} label={props.label_name || "Nom"}>
              <input
                required={nameMode === "required"}
                data-testid="contact-name"
                autoComplete="name"
                placeholder={props.placeholder_name || "Camille Durand"}
                value={form.name}
                onChange={setField("name")}
                className={FIELD}
              />
            </Field>
            <Field mode={emailMode} label={props.label_email || "Email"}>
              <input
                required={emailMode === "required"}
                type="email"
                data-testid="contact-email"
                autoComplete="email"
                placeholder={props.placeholder_email || "camille@entreprise.fr"}
                value={form.email}
                onChange={setField("email")}
                className={FIELD}
              />
            </Field>
            <Field mode={phoneMode} label={props.label_phone || "Téléphone"}>
              <input
                required={phoneMode === "required"}
                type="tel"
                data-testid="contact-phone"
                autoComplete="tel"
                placeholder={props.placeholder_phone || "06 12 34 56 78"}
                value={form.phone}
                onChange={setField("phone")}
                className={FIELD}
              />
            </Field>
          </div>

          {planOptions.length > 0 && (
            <fieldset>
              <legend className={LABEL}>{props.label_plan || "Plan envisagé"}</legend>
              <div className="flex flex-wrap gap-2">
                {planOptions.map((option) => {
                  const on = plan === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setPlan(on ? null : option)}
                      className={`font-display rounded-full py-[9px] px-[15px] text-[14px] border-control transition-colors duration-150 ${
                        on ? "border-gold bg-gold/[0.14] text-ink" : "border-ink/[0.16] text-ink hover:border-ink/40"
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          <label className="block">
            <span className={`block ${LABEL}`}>{props.label_message || "Votre projet"}</span>
            <textarea
              required
              rows={4}
              data-testid="contact-message"
              placeholder={props.placeholder_message || "Activité, objectif, échéance…"}
              value={form.message}
              onChange={setField("message")}
              className={`${FIELD} resize-y`}
            />
          </label>

          <button
            type="submit"
            disabled={status !== "idle"}
            data-testid="contact-submit"
            className="text-center bg-blush text-ink rounded-btn py-3.5 font-display font-medium text-[15px] hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {status === "sending"
              ? (props.sending_label || "Envoi en cours…")
              : status === "sent"
                ? (props.sent_label || "Demande envoyée ✓")
                : props.submit_label || "Envoyer ma demande"}
          </button>
        </form>
      </div>
    </section>
  );
}
